import { useLingui } from "@lingui/react/macro";
import Form from "@/components/Form/Form";
import InputField from "@medoma/components/InputField/InputField";
import { useFormContext } from "react-hook-form";
import { addDays, isToday } from "date-fns";
import { useEffect } from "react";
import { PlainButton } from "@medoma/components/Button/Button";
import styles from "./DateInput.module.scss";
import { machineFormat } from "@medoma/models/date-and-time";
import { dateMonthAndYear } from "@/Utils/DateUtils";

export type IDateInputFields = { startDate: string; endDate: string };

export type IQuickSelection = {
  label: string;
  date: Date;
};

export type IDateInput = {
  hasEndDate?: boolean;
  minDate?: Date;
  quickSelections?: IQuickSelection[];
};

export const DateInput = ({
  hasEndDate = false,
  minDate = new Date(),
  quickSelections,
}: IDateInput) => {
  const {
    formState: { errors },
    register,
    setValue,
    unregister,
    watch,
  } = useFormContext<IDateInputFields>();

  const { t } = useLingui();

  const defaultQuickSelections = [
    { label: t`Idag`, date: new Date() },
    { label: t`Imorgon`, date: addDays(new Date(), 1) },
  ];

  const selections = quickSelections || defaultQuickSelections;
  const hasQuickSelections = selections && selections.length > 0;

  const minDateMessageString = isToday(minDate)
    ? t`idag`
    : dateMonthAndYear(minDate);

  const startDateOptions = {
    required: {
      value: true,
      message: hasEndDate ? t`Ange startdatum` : t`Ange datum`,
    },
    min: {
      value: machineFormat(minDate, "yyyy-MM-dd"),
      message: hasEndDate
        ? t`Startdatumet behöver vara ${minDateMessageString} eller senare`
        : t`Datumet behöver vara ${minDateMessageString} eller senare`,
    },
  };

  useEffect(() => {
    if (!hasEndDate) {
      unregister("endDate");
    }
  }, [hasEndDate, unregister]);

  const handleShortcutButtonClick = (date: Date) => {
    setValue("startDate", machineFormat(date, "yyyy-MM-dd"));
  };

  const renderShortcutButtons = () => (
    <div className={styles.shortcutButtons}>
      {selections.map((selection) => (
        <PlainButton
          key={selection.label}
          size="small"
          onClick={() => handleShortcutButtonClick(selection.date)}
        >
          {selection.label}
        </PlainButton>
      ))}
    </div>
  );

  return hasEndDate ? (
    <Form.Row type="flex">
      <div>
        <InputField
          label={t`Startdatum`}
          showOptionalLabel={false}
          errorMessage={errors.startDate?.message}
          type="date"
          {...register(`startDate`, startDateOptions)}
        />
        {hasQuickSelections ? renderShortcutButtons() : null}
      </div>
      <InputField
        errorMessage={errors.endDate?.message}
        key="endDate"
        label={t`Slutdatum`}
        showOptionalLabel
        type="date"
        min={watch("startDate")}
        {...register("endDate")}
      />
    </Form.Row>
  ) : (
    <div>
      <InputField
        label={t`Datum`}
        showOptionalLabel={false}
        errorMessage={errors.startDate?.message}
        type="date"
        {...register(`startDate`, startDateOptions)}
      />
      {hasQuickSelections ? renderShortcutButtons() : null}
    </div>
  );
};
